/**
 * <ActivityCorrectedCopy> — la copie corrigée, ses pastilles cliquables et le point choisi (organism).
 *
 * Le port du modèle tuteur de la TodoModal (`CorrectionMarkersOverlay` + le panneau des points) :
 *  - la page brute de la copie, en grand, et dessus une pastille par point, posée au coin haut-droit
 *    de sa zone — hors de l'écriture, la réponse reste lisible ;
 *  - toucher une pastille choisit son point : sa zone s'encadre sur la copie, sa carte s'affiche
 *    sous la page ; « ‹ Point 2 sur 3 › » parcourt les points et tourne la page au besoin ;
 *  - l'œil masque les pastilles pour relire la copie nue ; la loupe ouvre la page en plein écran.
 *
 * L'organism ne sait rien de qui regarde — parent aujourd'hui, élève demain (la correction
 * in-flow) : aucun mot d'un public, tout arrive en props. Il ne sert que quand chaque point porte
 * son ancre ; sinon `<ActivityWork>` garde l'image cuite et la liste des points.
 *
 * Deux mises en page :
 *  - par défaut, la page prend la largeur et le point se lit dessous — la fiche défile (téléphone) ;
 *  - `fill` : l'organism remplit la place que lui donne son parent, et la page entière y tient.
 *    Le point se lit à côté d'elle (panneau qui porte aussi les commandes) ou dessous (une seule
 *    ligne de commandes) : on prend la disposition qui donne la plus grande page (grand écran).
 *
 * Les commandes (le point, la page, l'œil, la loupe) ont leur ligne par défaut. `controls="overlay"` les
 * pose sur la page, en une pastille flottante au pied de la copie, sans ligne à elles : le centre du live
 * du masteur, où la copie prend toute la place (Wajih, round 3). « Point 1 sur 1 » n'y paraît pas.
 *
 * `bare` : la copie nue, en grand — ni pastilles, ni point, ni œil ; la page et la loupe seules (une
 * présentation : les élèves voient l'écran du masteur). Sans effet par défaut.
 *
 * Les positions sont des fractions de la page : la boîte prend le rapport de la photo
 * (`Image.getSize`), l'image la remplit exactement, et une ancre devient un pourcentage — aucun
 * calcul de « contain » à refaire, à aucune taille.
 */
import React, { useEffect, useState } from "react"
import { Image, useWindowDimensions, View, type LayoutChangeEvent } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { type UIActivityCorrectionAnchor, type UIActivityCorrectionPoint } from "../../../ports/primary/ui/types"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { ActivityCorrectionMarker, CORRECTION_MARKER_SIZE } from "../../molecules/ActivityCorrectionMarker"
import { ActivityCorrectionPoint, CORRECTION_POINT_FACES } from "../../molecules/ActivityCorrectionPoint"
import { Card } from "../../molecules/Card"
import { MediaDisplay } from "../MediaDisplay"
import {
    BELOW_CARD_SHARE,
    SCROLL_PAGE_MAX_SHARE,
    SIDE_GAP,
    SIDE_PANEL_WIDTH,
    fitPage,
    pointGoesBeside,
    type Size,
} from "./copyLayout"

/** Le rapport largeur / hauteur de chaque photo déjà mesurée : la même copie se rouvre souvent. */
const aspectCache: Record<string, number> = {}

/** Le rapport d'une photo, `null` tant qu'il n'est pas connu (les pastilles attendent). */
function usePageAspect(uri: string | undefined): number | null {
    const [aspect, setAspect] = useState<number | null>(uri != null ? (aspectCache[uri] ?? null) : null)

    useEffect(() => {
        if (uri == null) return
        if (aspectCache[uri] != null) {
            setAspect(aspectCache[uri]!)
            return
        }
        setAspect(null)
        let mounted = true
        Image.getSize(
            uri,
            (width, height) => {
                if (width <= 0 || height <= 0) return
                aspectCache[uri] = width / height
                if (mounted) setAspect(width / height)
            },
            // Mesure impossible : la page s'affiche sans pastilles, la carte du point reste lisible.
            () => undefined
        )
        return () => {
            mounted = false
        }
    }, [uri])

    return aspect
}

const NO_POINTS: UIActivityCorrectionPoint[] = []

/** Une fraction de page en pourcentage, gardée dans la page pour qu'aucune pastille n'en sorte. */
function percent(fraction: number, margin = 0): `${number}%` {
    return `${Math.min(Math.max(fraction, margin), 1 - margin) * 100}%`
}

export type ActivityCorrectedCopyProps = {
    /** Les pages brutes de la copie, dans l'ordre. */
    pages: string[]
    /** Les points, dans l'ordre des pastilles — chacun avec son ancre. Absents : la copie nue (`bare`). */
    points?: UIActivityCorrectionPoint[]
    /**
     * Remplir la place donnée par le parent (qui doit donner une hauteur) : la page tient entière,
     * le point se lit à côté ou dessous, selon ce qui donne la plus grande page. Défaut `false` :
     * la page prend la largeur et c'est la fiche qui défile.
     */
    fill?: boolean
    /** Où vont les commandes : leur ligne (défaut), ou posées sur la page (`overlay`). */
    controls?: "rows" | "overlay"
    /** La copie nue : ni pastilles, ni point, ni œil (une présentation). Défaut `false`. */
    bare?: boolean
    testID?: string
}

/** Un `onLayout` qui ne relance un rendu que si la taille a vraiment changé. */
function useMeasured(): [Size | null, (event: LayoutChangeEvent) => void] {
    const [size, setSize] = useState<Size | null>(null)
    const onLayout = (event: LayoutChangeEvent) => {
        const { width, height } = event.nativeEvent.layout
        setSize((previous) =>
            previous != null && previous.width === width && previous.height === height ? previous : { width, height }
        )
    }
    return [size, onLayout]
}

export function ActivityCorrectedCopy(props: ActivityCorrectedCopyProps) {
    const { pages, points: allPoints = NO_POINTS, fill = false, controls = "rows", bare = false, testID } = props
    const points = bare ? NO_POINTS : allPoints
    const overlay = controls === "overlay"

    const [selectedIndex, setSelectedIndex] = useState<number | null>(points[0]?.index ?? null)
    const [page, setPage] = useState(points[0]?.anchor?.page ?? 0)
    const [markersVisible, setMarkersVisible] = useState(true)
    const [zoomOpen, setZoomOpen] = useState(false)
    const [root, onRootLayout] = useMeasured()
    const [area, onAreaLayout] = useMeasured()

    const pageUri = pages[page]
    const aspect = usePageAspect(pageUri)
    const windowHeight = useWindowDimensions().height

    if (pages.length === 0 || (points.length === 0 && !bare)) return null

    const position = points.findIndex((point) => point.index === selectedIndex)
    const selectedPoint = position >= 0 ? points[position] : undefined
    const pagePoints = points.filter((point) => point.anchor?.page === page)
    const side = !bare && fill && root != null && pointGoesBeside(root, aspect ?? 3 / 4)

    /** Choisir un point, c'est aussi tourner la page où il se trouve. */
    const select = (point: UIActivityCorrectionPoint | undefined) => {
        if (point == null) return
        setSelectedIndex(point.index)
        if (point.anchor != null) setPage(point.anchor.page)
    }

    const pageNav =
        pages.length > 1 ? (
            <Row gap="0">
                <IconButton
                    iconName="chevron-left"
                    appearance="transparent"
                    iconColor="primary"
                    disabled={page === 0}
                    onPress={() => setPage(page - 1)}
                    accessibilityLabel="Page précédente"
                />
                <Text size="text-sm" color="text-muted">
                    Page {page + 1} sur {pages.length}
                </Text>
                <IconButton
                    iconName="chevron-right"
                    appearance="transparent"
                    iconColor="primary"
                    disabled={page === pages.length - 1}
                    onPress={() => setPage(page + 1)}
                    accessibilityLabel="Page suivante"
                />
            </Row>
        ) : null
    const tools = (
        <Row gap="0">
            {!bare && (
                <IconButton
                    iconName={markersVisible ? "eye-slash" : "eye"}
                    appearance="transparent"
                    iconColor="primary"
                    onPress={() => setMarkersVisible(!markersVisible)}
                    accessibilityLabel={markersVisible ? "Masquer les pastilles" : "Afficher les pastilles"}
                />
            )}
            <IconButton
                iconName="search-plus"
                appearance="transparent"
                iconColor="primary"
                onPress={() => setZoomOpen(true)}
                accessibilityLabel="Agrandir la copie"
            />
        </Row>
    )
    const toolbar = (
        <Row justify="between">
            {pageNav ?? <View />}
            {tools}
        </Row>
    )
    const pageControls = (
        <Row gap="2">
            {pageNav}
            {tools}
        </Row>
    )

    // La zone où la page doit tenir — la page attend qu'elle soit mesurée. À côté du panneau, elle se
    // déduit de la racine : la page et son point forment un seul bloc centré, au lieu d'une page
    // perdue au milieu de la largeur et d'un panneau collé au bord.
    const sideArea =
        side && root != null ? { width: root.width - SIDE_PANEL_WIDTH - SIDE_GAP, height: root.height } : null
    const fillArea = sideArea ?? area
    // Sans `fill`, la page prend la largeur, mais jamais plus que les sept dixièmes de l'écran en
    // hauteur : sur une tablette, une copie pleine largeur ferait 1 300 px de haut.
    const scrollArea = root != null ? { width: root.width, height: windowHeight * SCROLL_PAGE_MAX_SHARE } : null
    const pageArea = fill ? fillArea : scrollArea
    const box = pageArea != null ? fitPage(pageArea, aspect ?? 3 / 4) : null
    const pager = selectedPoint != null && (
        <Row justify="between">
            <IconButton
                iconName="chevron-left"
                appearance="transparent"
                iconColor="primary"
                disabled={position === 0}
                onPress={() => select(points[position - 1])}
                accessibilityLabel="Point précédent"
            />
            <Text size="text-sm" weight="semibold">
                Point {position + 1} sur {points.length}
            </Text>
            <IconButton
                iconName="chevron-right"
                appearance="transparent"
                iconColor="primary"
                disabled={position === points.length - 1}
                onPress={() => select(points[position + 1])}
                accessibilityLabel="Point suivant"
            />
        </Row>
    )
    const copy =
        box === null ? null : (
            <View style={styles.page(box)}>
                {pageUri != null && (
                    <Image
                        source={{ uri: pageUri }}
                        resizeMode="contain"
                        style={styles.image}
                        accessibilityLabel={`Copie, page ${page + 1}`}
                    />
                )}
                {markersVisible &&
                    aspect != null &&
                    pagePoints.map((point) => {
                        const anchor = point.anchor!
                        const selected = point.index === selectedIndex
                        return (
                            <React.Fragment key={point.index}>
                                {selected && (
                                    <View
                                        pointerEvents="none"
                                        style={styles.zone(anchor, CORRECTION_POINT_FACES[point.type].zone)}
                                    />
                                )}
                                <ActivityCorrectionMarker
                                    index={point.index}
                                    type={point.type}
                                    selected={selected}
                                    onPress={() => select(point)}
                                    style={styles.marker(anchor)}
                                />
                            </React.Fragment>
                        )
                    })}
                {overlay && (
                    <View style={styles.overlayBar(fill)} pointerEvents="box-none">
                        <Card p="0" elevation="floating" style={styles.overlayPill}>
                            <Row gap="1" align="center">
                                {points.length > 1 && pager}
                                {pageNav}
                                {tools}
                            </Row>
                        </Card>
                    </View>
                )}
            </View>
        )

    const card = selectedPoint != null && <ActivityCorrectionPoint point={selectedPoint} />

    const zoom = zoomOpen && (
        <MediaDisplay
            visible
            kind="image"
            images={pages.map((uri) => ({ uri }))}
            initialIndex={page}
            onClose={() => setZoomOpen(false)}
        />
    )

    if (!fill) {
        return (
            <Stack gap="3" testID={testID}>
                {!overlay && toolbar}
                <View style={styles.scrollPage} onLayout={onRootLayout}>
                    {copy}
                </View>
                {selectedPoint != null && (
                    <Stack gap="2">
                        {!overlay && pager}
                        {card}
                    </Stack>
                )}
                {zoom}
            </Stack>
        )
    }

    const copyArea = (
        <View style={styles.copyArea} onLayout={onAreaLayout}>
            {copy}
        </View>
    )

    return (
        <View style={styles.fillRoot(side)} onLayout={onRootLayout} testID={testID}>
            {side ? (
                <>
                    {copy}
                    <View style={styles.sidePanel}>
                        {!overlay && toolbar}
                        {!overlay && pager}
                        <ScrollView style={styles.flex} showsVerticalScrollIndicator={false}>
                            {card}
                        </ScrollView>
                    </View>
                </>
            ) : (
                <>
                    {/* Dessous, une seule ligne de commandes : les points à gauche, la page à droite. */}
                    {!overlay && (
                        <Row justify="between">
                            {pager}
                            {pageControls}
                        </Row>
                    )}
                    {copyArea}
                    <ScrollView style={styles.cardBelow} showsVerticalScrollIndicator={false}>
                        {card}
                    </ScrollView>
                </>
            )}
            {zoom}
        </View>
    )
}

/** La pastille ne déborde pas de la page : son centre reste à une demi-pastille du bord. */
const MARKER_MARGIN = 0.04

const styles = StyleSheet.create((theme) => ({
    fillRoot: (side: boolean) => ({
        flex: 1,
        minHeight: 0,
        flexDirection: side ? ("row" as const) : ("column" as const),
        justifyContent: side ? ("center" as const) : ("flex-start" as const),
        gap: theme.spacing[side ? "5" : "3"],
    }),
    flex: {
        flex: 1,
    },
    copyArea: {
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        alignItems: "center" as const,
    },
    sidePanel: {
        width: SIDE_PANEL_WIDTH,
        gap: theme.spacing["2"],
    },
    /** Sous la page, la carte ne prend jamais plus des deux cinquièmes : la copie reste lisible. */
    cardBelow: {
        flexGrow: 0,
        maxHeight: `${BELOW_CARD_SHARE * 100}%` as const,
    },
    /**
     * La pastille des commandes (`controls="overlay"`) : au pied de la page quand elle tient entière
     * (`fill`) ; en haut quand la page défile, pour rester à portée.
     */
    overlayBar: (fill: boolean) => ({
        position: "absolute" as const,
        left: 0,
        right: 0,
        ...(fill ? { bottom: theme.spacing["2"] } : { top: theme.spacing["2"] }),
        alignItems: "center" as const,
    }),
    overlayPill: { borderRadius: theme.radius.full, paddingHorizontal: theme.spacing["1"] },
    /** La zone de la page sans `fill` : toute la largeur, la page centrée dedans. */
    scrollPage: {
        alignItems: "center" as const,
    },
    page: (box: Size) => ({
        width: box.width,
        height: box.height,
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-subtle"],
    }),
    image: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        width: "100%" as const,
        height: "100%" as const,
        borderRadius: theme.radius.md,
    },
    marker: (anchor: UIActivityCorrectionAnchor) => ({
        position: "absolute" as const,
        left: percent(anchor.x + anchor.w, MARKER_MARGIN),
        top: percent(anchor.y, MARKER_MARGIN),
        marginLeft: -CORRECTION_MARKER_SIZE / 2,
        marginTop: -CORRECTION_MARKER_SIZE / 2,
    }),
    zone: (anchor: UIActivityCorrectionAnchor, color: SemanticColorKey) => ({
        position: "absolute" as const,
        left: percent(anchor.x),
        top: percent(anchor.y),
        width: percent(anchor.w),
        height: percent(anchor.h),
        borderWidth: 2,
        borderStyle: "dashed" as const,
        borderRadius: theme.radius.sm,
        borderColor: theme.colors[color],
    }),
}))
